<template>
  <section class="tutorials-page">
    <div class="container-fluid py-4">
      <header class="tutorials-header mb-4">
        <div class="tutorials-icon">
          <AppIcon name="bi-play-btn" class="bi bi-play-btn" />
        </div>
        <div>
          <h3 class="mb-1">{{ $t('tutorials') }}</h3>
          <p class="text-muted mb-0">{{ $t('tutorials_description') }}</p>
        </div>
      </header>

      <div class="tutorials-layout">
        <main class="tutorial-main">
          <article v-if="activeTutorial" class="card tutorial-card">
            <div class="tutorial-player">
              <iframe
                :src="`https://www.youtube.com/embed/${activeTutorial.youtubeId}?rel=0`"
                :title="$t(activeTutorial.title)"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
              ></iframe>
            </div>
            <div class="card-body d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
              <div>
                <h5 class="mb-1">{{ $t(activeTutorial.title) }}</h5>
                <p class="text-muted mb-0">{{ $t('tutorial_video_youtube_description') }}</p>
              </div>
              <a
                class="btn btn-dark tutorial-link"
                :href="`https://www.youtube.com/watch?v=${activeTutorial.youtubeId}`"
                target="_blank"
                rel="noopener noreferrer"
              >
                <AppIcon name="bi-youtube" class="bi bi-youtube me-2" />{{ $t('watch_on_youtube') }}
              </a>
            </div>
          </article>
        </main>

        <aside class="tutorial-sidebar">
          <section class="tutorial-library" :aria-label="$t('tutorial_video_library')">
            <div class="tutorial-library-header">
              <h5 class="mb-0">{{ $t('tutorial_video_library') }}</h5>
              <span class="tutorial-count">{{ tutorials.length }}</span>
            </div>

            <div class="tutorial-list">
              <button
                v-for="tutorial in tutorials"
                :key="tutorial.youtubeId"
                type="button"
                class="tutorial-item"
                :class="{ active: tutorial.youtubeId === activeTutorialId }"
                @click="selectTutorial(tutorial.youtubeId)"
              >
                <span class="tutorial-thumbnail">
                  <img :src="thumbnailUrl(tutorial.youtubeId)" :alt="$t(tutorial.title)" loading="lazy">
                  <AppIcon name="bi-play-fill" class="bi bi-play-fill" />
                </span>
                <span class="tutorial-item-copy">
                  <span class="tutorial-item-title">{{ $t(tutorial.title) }}</span>
                </span>
              </button>
            </div>
          </section>
        </aside>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'Tutorials',
  data() {
    return {
      activeTutorialId: 'IB5U2A64oio',
      tutorials: [
        {
          title: 'tutorial_video_1',
          youtubeId: 'IB5U2A64oio'
        },
        {
          title: 'tutorial_video_2',
          youtubeId: 'YULhYjJ-fxk'
        },
        {
          title: 'tutorial_video_3',
          youtubeId: 'Z95B0VNpksE'
        },
        {
          title: 'tutorial_video_4',
          youtubeId: 'TOdv6q6fXnE'
        }
      ]
    }
  },
  computed: {
    activeTutorial() {
      return this.tutorials.find(tutorial => tutorial.youtubeId === this.activeTutorialId) || this.tutorials[0]
    }
  },
  mounted() {
    document.title = 'Tutorials | Gstocky'
  },
  methods: {
    thumbnailUrl(youtubeId) {
      return `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`
    },
    selectTutorial(youtubeId) {
      this.activeTutorialId = youtubeId
    }
  }
}
</script>

<style scoped>
.tutorials-header {
  align-items: center;
  display: flex;
  gap: 1rem;
}

.tutorials-icon {
  align-items: center;
  background: #fff0e7;
  border-radius: 8px;
  color: #dc3545;
  display: flex;
  flex: 0 0 3.25rem;
  font-size: 1.75rem;
  height: 3.25rem;
  justify-content: center;
}

.tutorials-layout {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: minmax(0, 1fr) minmax(19rem, 24rem);
}

.tutorial-main {
  min-width: 0;
}

.tutorial-card {
  border: 1px solid rgba(33, 37, 41, 0.1);
  border-radius: 8px;
  box-shadow: 0 0.35rem 1.5rem rgba(33, 37, 41, 0.08);
  overflow: hidden;
}

.tutorial-player {
  aspect-ratio: 16 / 9;
  background: #111;
  width: 100%;
}

.tutorial-player iframe {
  border: 0;
  display: block;
  height: 100%;
  width: 100%;
}

.tutorial-link {
  flex: 0 0 auto;
  white-space: nowrap;
}

.tutorial-sidebar {
  display: grid;
  gap: 1rem;
  min-width: 0;
}

.tutorial-library {
  background: #fff;
  border: 1px solid rgba(33, 37, 41, 0.1);
  border-radius: 8px;
}

.tutorial-library {
  overflow: hidden;
}

.tutorial-library-header {
  align-items: center;
  border-bottom: 1px solid rgba(33, 37, 41, 0.1);
  display: flex;
  justify-content: space-between;
  padding: 1rem;
}

.tutorial-count {
  align-items: center;
  background: #f1f3f5;
  border-radius: 6px;
  color: #495057;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 600;
  height: 1.75rem;
  justify-content: center;
  min-width: 1.75rem;
  padding: 0 0.45rem;
}

.tutorial-list {
  display: grid;
  max-height: calc(100vh - 18rem);
  overflow-y: auto;
  padding: 0.5rem;
}

.tutorial-item {
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: inherit;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 8rem minmax(0, 1fr);
  padding: 0.55rem;
  text-align: left;
  width: 100%;
}

.tutorial-item:hover,
.tutorial-item.active {
  background: #f5f7f9;
}

.tutorial-thumbnail {
  aspect-ratio: 16 / 9;
  background: #111;
  border-radius: 6px;
  display: block;
  overflow: hidden;
  position: relative;
}

.tutorial-thumbnail img {
  display: block;
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.tutorial-thumbnail i {
  align-items: center;
  background: rgba(17, 17, 17, 0.72);
  border-radius: 50%;
  color: #fff;
  display: flex;
  font-size: 1rem;
  height: 1.8rem;
  justify-content: center;
  left: 50%;
  padding-left: 0.1rem;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 1.8rem;
}

.tutorial-item-copy {
  display: grid;
  gap: 0.45rem;
  min-width: 0;
}

.tutorial-item-title {
  color: #212529;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.3;
}

@media (max-width: 1199px) {
  .tutorials-layout {
    grid-template-columns: 1fr;
  }

  .tutorial-list {
    max-height: none;
  }
}

@media (max-width: 575px) {
  .tutorial-item {
    grid-template-columns: 6.75rem minmax(0, 1fr);
  }
}
</style>
